import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EnhancedCharacter } from '../../utils/ClassComponentRegistry';
import { resolveEquipmentReference, ResolvedEquipmentItem } from '../../utils/equipmentResolver';
import { getWeaponDamageDisplay, getProficiency } from '../../utils/weaponDamage';

interface EquipmentSlotsProps {
  enhancedCharacter?: EnhancedCharacter;
}

const EquipmentSlots: React.FC<EquipmentSlotsProps> = ({ enhancedCharacter }) => {
  const [resolvedEquipment, setResolvedEquipment] = useState<Map<string, ResolvedEquipmentItem>>(new Map());
  const [loading, setLoading] = useState(false);

  const character = enhancedCharacter?.character;
  const equipmentReferences = character?.inventory?.equipmentReferences || [];
  
  // Get only equipped references
  const equippedReferences = equipmentReferences.filter(ref => ref.equippedCount > 0);

  // Resolve equipped equipment references
  useEffect(() => {
    const resolveEquippedItems = async () => {
      if (equippedReferences.length === 0) return;
      
      setLoading(true);
      const newResolved = new Map<string, ResolvedEquipmentItem>();
      
      for (const ref of equippedReferences) {
        if (!resolvedEquipment.has(ref.itemId)) {
          try {
            const resolved = await resolveEquipmentReference(ref.itemId);
            if (resolved) {
              newResolved.set(ref.itemId, resolved);
            }
          } catch (error) {
            console.error(`Failed to resolve equipped equipment ${ref.itemId}:`, error);
          }
        }
      }
      
      if (newResolved.size > 0) {
        setResolvedEquipment(prev => {
          const updated = new Map(prev);
          newResolved.forEach((value, key) => {
            updated.set(key, value);
          });
          return updated;
        });
      }
      setLoading(false);
    };

    resolveEquippedItems();
  }, [equippedReferences, resolvedEquipment]);

  // If no enhanced character, show placeholder
  if (!enhancedCharacter?.character) {
    return (
      <div className="equipment-slots">
        <p className="equipment-slots__placeholder">
          Equipped items will appear here for enhanced characters.
        </p>
      </div>
    );
  }

  const handleUnequip = (itemId: string, equippedCount: number) => {
    if (enhancedCharacter.unequipItemByReference) {
      enhancedCharacter.unequipItemByReference(itemId, equippedCount);
    }
  };

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getSlotsByType = () => {
    const slotsByType = {
      weapon: [] as Array<{ref: any, resolved: ResolvedEquipmentItem}>,
      armor: [] as Array<{ref: any, resolved: ResolvedEquipmentItem}>,
      loot: [] as Array<{ref: any, resolved: ResolvedEquipmentItem}>
    };

    equippedReferences.forEach(ref => {
      const resolved = resolvedEquipment.get(ref.itemId);
      if (resolved) {
        const type = resolved.type as keyof typeof slotsByType;
        if (slotsByType[type]) {
          slotsByType[type].push({ ref, resolved });
        }
      }
    });

    return slotsByType;
  };

  const slots = getSlotsByType();

  return (
    <div className="equipment-slots">
        {loading && <div className="equipment-slots__loading">Loading equipped items...</div>}
        {equippedReferences.length === 0 ? (
          <div className="equipment-slots__empty">
            <p>No items equipped.</p>
            <small>Use the Content Library to add items, then equip them here.</small>
          </div>
        ) : (
          <div className="equipment-slots__grid">
            {/* Weapon Slot */}
            <div className="equipment-slots__category">
              <motion.h4 
                className="equipment-slots__category-title"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                Weapons
              </motion.h4>
              <div className="equipment-slots__items">
                <AnimatePresence mode="popLayout">
                  {slots.weapon.length > 0 ? (
                    slots.weapon.map(({ ref, resolved }, index) => {
                      const proficiency = getProficiency(character!);
                      const damageDisplay = getWeaponDamageDisplay(resolved, proficiency);
                      return (
                        <motion.div
                          key={ref.itemId}
                          className="equipment-slot"
                          initial={{ opacity: 0, y: 20, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -20, scale: 0.9 }}
                          transition={{ delay: 0.3 + index * 0.1, duration: 0.3 }}
                          layout
                        >
                          <div className="equipment-slot__header">
                            <span className="equipment-slot__name" title={capitalizeFirstLetter(resolved.name)}>{capitalizeFirstLetter(resolved.name)}</span>
                            <span className="equipment-slot__count">×{ref.equippedCount}</span>
                            <motion.button
                              className="equipment-slot__unequip"
                              onClick={() => handleUnequip(ref.itemId, ref.equippedCount)}
                              title="Unequip item"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              ×
                            </motion.button>
                          </div>
                          <div className="equipment-slot__damage">
                            <span className="equipment-slot__damage-label">Damage:</span>
                            <span className="equipment-slot__damage-value">{damageDisplay}</span>
                          </div>
                        </motion.div>
                      );
                    })
                  ) : (
                    <motion.div
                      className="equipment-slot equipment-slot--empty"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <span className="equipment-slot__empty-text">No weapon equipped</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Armor Slot */}
            <div className="equipment-slots__category">
              <motion.h4 
                className="equipment-slots__category-title"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                Armor
              </motion.h4>
              <div className="equipment-slots__items">
                <AnimatePresence mode="popLayout">
                  {slots.armor.length > 0 ? (
                    slots.armor.map(({ ref, resolved }, index) => (
                      <motion.div
                        key={ref.itemId}
                        className="equipment-slot"
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.9 }}
                        transition={{ delay: 0.5 + index * 0.1, duration: 0.3 }}
                        layout
                      >
                        <div className="equipment-slot__header">
                          <span className="equipment-slot__name">{capitalizeFirstLetter(resolved.name)}</span>
                          <span className="equipment-slot__count">×{ref.equippedCount}</span>
                          <motion.button
                            className="equipment-slot__unequip"
                            onClick={() => handleUnequip(ref.itemId, ref.equippedCount)}
                            title="Unequip item"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            ×
                          </motion.button>
                        </div>
                        {((resolved as any).baseArmorScore !== undefined || (resolved as any).baseThresholds) && (
                          <div className="equipment-slot__armor-stats">
                            {(resolved as any).baseArmorScore !== undefined && (
                              <div className="equipment-slot__stat">
                                <span className="equipment-slot__stat-label">Armor:</span>
                                <span className="equipment-slot__stat-value">{(resolved as any).baseArmorScore}</span>
                              </div>
                            )}
                            {(resolved as any).baseThresholds?.major !== undefined && (
                              <div className="equipment-slot__stat">
                                <span className="equipment-slot__stat-label">Major:</span>
                                <span className="equipment-slot__stat-value">{(resolved as any).baseThresholds.major}</span>
                              </div>
                            )}
                            {(resolved as any).baseThresholds?.severe !== undefined && (
                              <div className="equipment-slot__stat">
                                <span className="equipment-slot__stat-label">Severe:</span>
                                <span className="equipment-slot__stat-value">{(resolved as any).baseThresholds.severe}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </motion.div>
                    ))
                  ) : (
                    <motion.div 
                      className="equipment-slot equipment-slot--empty"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      <span className="equipment-slot__empty-text">No armor equipped</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Loot Items */}
            <div className="equipment-slots__category">
              <motion.h4
                className="equipment-slots__category-title"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                Loot
              </motion.h4>
              <div className="equipment-slots__items">
                <AnimatePresence mode="popLayout">
                  {slots.loot.length > 0 ? (
                    slots.loot.map(({ ref, resolved }, index) => (
                      <motion.div
                        key={ref.itemId}
                        className="equipment-slot"
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.9 }}
                        transition={{ delay: 0.7 + index * 0.1, duration: 0.3 }}
                        layout
                      >
                        <div className="equipment-slot__header">
                          <span className="equipment-slot__name">{capitalizeFirstLetter(resolved.name)}</span>
                          <span className="equipment-slot__count">×{ref.equippedCount}</span>
                          <motion.button
                            className="equipment-slot__unequip"
                            onClick={() => handleUnequip(ref.itemId, ref.equippedCount)}
                            title="Unequip item"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            ×
                          </motion.button>
                        </div>
                        {/* Display modifiers if available */}
                        {resolved.modifiers && resolved.modifiers.length > 0 && (
                          <div className="equipment-slot__modifiers">
                            {resolved.modifiers.map((mod, modIndex) => {
                              const target = mod.target.split('.').pop();
                              const value = typeof mod.value === 'number' ? mod.value : mod.value;
                              const formattedValue = typeof value === 'number' && value >= 0 ? `+${value}` : `${value}`;
                              return (
                                <span key={modIndex} className="equipment-slot__modifier">
                                  {formattedValue} {target}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </motion.div>
                    ))
                  ) : (
                    <motion.div
                      className="equipment-slot equipment-slot--empty"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      <span className="equipment-slot__empty-text">No loot equipped</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        )}

        {equippedReferences.length > 0 && (
          <motion.div
            className="equipment-slots__summary"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.3 }}
          >
            <small className="equipment-slots__summary-text">
              {equippedReferences.length} item{equippedReferences.length !== 1 ? 's' : ''} equipped
            </small>
          </motion.div>
        )}
    </div>
  );
};

export default EquipmentSlots;